import { io } from 'socket.io-client'
import ConfigAPI, { waitConfig } from '@/config/api'
 
/** Construir la URL de Socket.IO sin requerir baseWS en producción. */
function buildWsUrlFromBase(baseURL) {
  if (!baseURL) return null
 
  try {
    const apiUrl = new URL(baseURL, window.location.origin)
    const isLocal = ['localhost', '127.0.0.1'].includes(apiUrl.hostname)

    // Desarrollo local: API y Socket.IO usan puertos distintos.
    if (isLocal) {
      return `${apiUrl.protocol}//${apiUrl.hostname}:3001`
    }

    // Producción: kokue-api.wincampo.com → kokue-ws.wincampo.com
    if (apiUrl.hostname.includes('-api.')) {
      return `${apiUrl.protocol}//${apiUrl.hostname.replace('-api.', '-ws.')}`
    }
  } catch (error) {
    console.error('Error construyendo WS URL desde baseURL:', error)
  }

  return null
}
 
class WebSocketService {
  constructor() {
    this.socket = null
    this.connected = false
    this.userId = null
    this.listeners = new Map()
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.config = {
      url: 'http://localhost:3001', // fallback inicial
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      transports: ['websocket', 'polling']
    }
  }
 
  /**
   * Conectar al servidor WebSocket
   * @param {string} userId - ID del usuario autenticado
   */
  async connect(userId) {
    if (this.connected && this.socket) {
      console.warn('⚠️ WebSocket ya está conectado')
      return
    }
 
    if (!userId) {
      console.error('❌ userId es requerido para conectar')
      return
    }
 
    this.userId = userId
    await waitConfig()
   
    // baseWS queda disponible para desarrollo; en producción se deriva de baseURL.
    this.config.url =
      ConfigAPI.wsURL ||
      buildWsUrlFromBase(ConfigAPI.baseURL) ||
      process.env.VUE_APP_WS_URL ||
      this.config.url
 
    console.log(`🔌 Conectando al WebSocket: ${this.config.url}`)
    console.log(`🔧 baseURL: ${ConfigAPI.baseURL}, wsURL: ${ConfigAPI.wsURL}`)
    console.log('🔧 Configuración Socket.IO:', this.config)
 
    this.socket = io(this.config.url, {
      ...this.config,
      auth: {
        userId: this.userId
      }
    })
 
    console.log('✨ Socket.IO creado:', this.socket)
    this._setupEventHandlers()
 
    // Conectar explícitamente (autoConnect está en false)
    console.log('🚀 Iniciando conexión...')
    this.socket.connect()
  }
 
  /**
   * Configurar manejadores de eventos de Socket.IO
   */
  _setupEventHandlers() {
    if (!this.socket) return
 
    // Evento: Conexión exitosa
    this.socket.on('connect', () => {
      this.connected = true
      this.reconnectAttempts = 0
      console.log('✅ WebSocket conectado:', this.socket.id)
      this._emit('connected', { socketId: this.socket.id })
    })
 
    // Evento: Confirmación del servidor
    this.socket.on('connected', (data) => {
      console.log('📡 Confirmación del servidor:', data)
    })
 
    // Evento: Desconexión
    this.socket.on('disconnect', (reason) => {
      this.connected = false
      console.log(`❌ WebSocket desconectado: ${reason}`)
      this._emit('disconnected', { reason })
    })
 
    // Evento: Error de conexión
    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++
      console.error(`⚠️ Error de conexión (intento ${this.reconnectAttempts}):`, error.message)
 
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ Máximo de reintentos alcanzado')
        this._emit('connection-failed', { error: error.message })
      }
    })
 
    // Evento: Trabajo completado (específico para sistema de colas)
    this.socket.on('job-completed', (data) => {
      console.log('✅ Job completado recibido:', data)
      this._emit('job-completed', data)
    })
 
    // Evento: Pong (respuesta al ping)
    this.socket.on('pong', (data) => {
      console.log('🏓 Pong recibido:', data)
    })
  }
 
  /**
   * Desconectar del servidor WebSocket
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Desconectando WebSocket...')
      this.socket.disconnect()
      this.socket = null
      this.connected = false
      this.userId = null
    }
  }
 
  /**
   * Suscribirse a un evento específico
   * @param {string} event - Nombre del evento
   * @param {Function} callback - Función a ejecutar cuando ocurra el evento
   * @returns {Function} Función para desuscribirse
   */
  on(event, callback) {
    if (typeof callback !== 'function') {
      console.error('❌ El callback debe ser una función')
      return () => {}
    }
 
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
 
    this.listeners.get(event).add(callback)
 
    // Retornar función para desuscribirse
    return () => {
      this.off(event, callback)
    }
  }
 
  /**
   * Desuscribirse de un evento
   * @param {string} event - Nombre del evento
   * @param {Function} callback - Función a remover
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }
 
  /**
   * Emitir evento a todos los listeners suscritos
   * @private
   */
  _emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`❌ Error en listener de evento "${event}":`, error)
        }
      })
    }
  }
 
  /**
   * Enviar ping al servidor para mantener conexión viva
   */
  ping() {
    if (this.socket && this.connected) {
      this.socket.emit('ping')
    }
  }
 
  /**
   * Verificar si está conectado
   */
  isConnected() {
    return this.connected && this.socket && this.socket.connected
  }
 
  /**
   * Obtener información de la conexión
   */
  getInfo() {
    return {
      connected: this.connected,
      socketId: this.socket?.id || null,
      userId: this.userId,
      reconnectAttempts: this.reconnectAttempts
    }
  }
 
  /**
   * Limpiar todos los listeners
   */
  clearListeners() {
    this.listeners.clear()
  }
}
 
// Exportar instancia singleton
export const webSocketService = new WebSocketService()
 
